import React, { useEffect } from 'react';
import { UpdateWorkspace } from '../../../../shared/types/workspaceTypes';
import { FlexCol } from '../../../../shared/FlexCol';
import { FlexRow } from '../../../../shared/FlexRow';
import { Tag } from '../../../../shared/styled-components/styled_tags';
import { Body, Headline } from '../../../../shared/styled-components/styled_text';
import { IconInput } from '../../../../shared/components/IconInput';
import styled from 'styled-components';
import axios from 'axios';
import { getApiUrl, getCandidatreeName } from '../../../../common/api/apiClient';
import { getSavedAuth } from '../../../../common/utils/storage';

interface Props {
  data: UpdateWorkspace;
  handleInputClick: () => void;
  onChange: (data: UpdateWorkspace) => void;
}

export const OnboardingStep2InputArea: React.FC<Props> = ({ data, handleInputClick, onChange }) => {
  const [treeValue, setTreeValue] = React.useState<string>(data.tree_signature ?? '');
  const [errorMessage, setErrorMessage] = React.useState<string>(data.tree_error ?? '');

  useEffect(() => {
    const getTempName = async () => {
      const tempName = await getCandidatreeName(data.name);
      setTreeValue(tempName.converted);
    };

    if (!data.tree_signature) {
      void getTempName();
    }
  }, []);

  useEffect(() => {
    const handleInputChange = async (signature: string) => {
      await axios.get(`${getApiUrl()}application-link/signature/check?q=${signature}`, {
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${getSavedAuth()?.access_token}`,
        },
      });
    };

    const signature = treeValue?.trim();
    if (!signature) {
      if (errorMessage) {
        setErrorMessage('');
      }
      onChange({ ...data, tree_signature: '', tree_error: '' });
      return;
    }

    if (signature.length > 20) {
      setErrorMessage('최대 20자까지 입력할 수 있어요.');
      onChange({ ...data, tree_signature: treeValue, tree_error: errorMessage });
      return;
    }

    handleInputChange(signature)
      .then(() => {
        setErrorMessage('');
        onChange({ ...data, tree_signature: treeValue, tree_error: '' });
      })
      .catch(e => {
        if (e.response?.status === 409) {
          setErrorMessage('이미 사용 중인 URL 주소예요.');
          onChange({ ...data, tree_signature: treeValue, tree_error: '이미 사용 중인 URL 주소예요.' });
        } else {
          if (!e.response?.data.message) return;
          if ((e.response?.data.message ?? '').includes('20')) {
            setErrorMessage('최대 20자까지 입력할 수 있어요.');
            onChange({ ...data, tree_signature: treeValue, tree_error: '최대 20자까지 입력할 수 있어요.' });
          } else {
            setErrorMessage('형식에 맞게 다시 입력해주세요.(한글, 영어 소문자, 숫자, ‘-’하이픈)');
            onChange({
              ...data,
              tree_signature: treeValue,
              tree_error: '형식에 맞게 다시 입력해주세요.(한글, 영어 소문자, 숫자, ‘-’하이픈)',
            });
          }
        }
      });
  }, [treeValue]);

  return (
    <Container>
      <FlexCol width={'100%'} justify={'flex-start'} align={'flex-start'} gap={'0.8rem'}>
        <FlexRow width={'100%'} justify={'flex-start'} align={'center'} gap={'0.6rem'}>
          <Tag color={'mono'}>선택</Tag>
          <Headline size={'m'}>채용 사이트 생성하기</Headline>
        </FlexRow>
        <Body size={'m'} style={{ color: ' var(--content-color-secondary)' }}>
          지원자가 회사 고유 채용 사이트로 지원자 지원서를 제출하면
          <br />
          대시보드에 자동 등록돼요.
        </Body>
      </FlexCol>
      <FlexCol width={'100%'} align={'flex-start'} gap={'0.4rem'}>
        <FlexRow width={'100%'} justify={'flex-start'} align={'center'} gap={'0.4rem'}>
          <Body size={'m'} style={{ color: ' var(--content-color-secondary)' }}>
            apply.candidate.im/
          </Body>
          <IconInput
            value={treeValue}
            onClick={handleInputClick}
            onChange={tree_signature => setTreeValue(tree_signature)}
            max={20}
            icons={{
              end2: {
                src: '/images/common/cancel_circle.svg',
                needsInput: true,
                onClick: () => setTreeValue(''),
              },
            }}
            showMaxCount
            placeholder={'예) candidate-company'}
          />
        </FlexRow>
        <Body size={'s'} style={{ color: ' var(--content-color-tertiary)', paddingLeft: '1.2rem' }}>
          한글, 영어 소문자, 숫자, ‘-’하이픈 조합
        </Body>
        {!!errorMessage && (
          <Body size={'s'} style={{ paddingLeft: '1.2rem', color: 'var(--error)' }}>
            {errorMessage}
          </Body>
        )}
      </FlexCol>
    </Container>
  );
};

const Container = styled.div`
  width: 100%;
  min-width: 49.8rem;
  display: flex;
  flex-direction: column;
  gap: 3.2rem;
`;
